import React, { useEffect, useState } from "react";
import { Card, Empty, Field, StageChip } from "../../dashboard/primitives";
import { ImageUploader, ImageGalleryUploader } from "../../dashboard/ImageUploader";
import LinksEditor from "../../dashboard/LinksEditor";
import {
  createCaseStudy,
  deleteCaseStudy,
  setPublished,
  subscribeAllCaseStudies,
  updateCaseStudy,
} from "../../caseStudies/api";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const linesToArray = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrayToLines = (a) => (a || []).join("\n");

const empty = {
  tag: "",
  title: "",
  description: "",
  color: "#1f1f1f",
  published: false,
  order: "",
  client: "",
  industry: "",
  year: new Date().getFullYear(),
  location: "",
  durationWeeks: "",
  challenge: "",
  approach: "",
  outcome: "",
  resultsText: "",
  deliverablesText: "",
  techStackText: "",
  heroImageUrl: "",
  galleryText: "",
  testimonialQuote: "",
  testimonialAuthor: "",
  testimonialRole: "",
  links: [],
};

const fromDoc = (d) => ({
  ...empty,
  ...d,
  order: d.order ?? "",
  year: d.year ?? new Date().getFullYear(),
  durationWeeks: d.durationWeeks ?? "",
  resultsText: arrayToLines(d.results),
  deliverablesText: arrayToLines(d.deliverables),
  techStackText: arrayToLines(d.techStack),
  galleryText: arrayToLines(d.gallery),
  testimonialQuote: d.testimonial?.quote || "",
  testimonialAuthor: d.testimonial?.author || "",
  testimonialRole: d.testimonial?.role || "",
  links: Array.isArray(d.links) && d.links.length
    ? d.links
    : [
        ...(d.liveUrl ? [{ type: "web", url: d.liveUrl }] : []),
        ...(d.caseUrl ? [{ type: "case", url: d.caseUrl }] : []),
      ],
});

const toPayload = (f) => ({
  tag: f.tag,
  title: f.title,
  description: f.description,
  color: f.color,
  published: !!f.published,
  order: f.order,
  client: f.client,
  industry: f.industry,
  year: f.year,
  location: f.location,
  durationWeeks: f.durationWeeks,
  challenge: f.challenge,
  approach: f.approach,
  outcome: f.outcome,
  results: linesToArray(f.resultsText),
  deliverables: linesToArray(f.deliverablesText),
  techStack: linesToArray(f.techStackText),
  heroImageUrl: f.heroImageUrl,
  gallery: linesToArray(f.galleryText),
  testimonial: { quote: f.testimonialQuote, author: f.testimonialAuthor, role: f.testimonialRole },
  links: f.links || [],
});

const Section = ({ title, children }) => (
  <div style={{ display: "grid", gap: 12, paddingTop: 8 }}>
    <div style={{ fontFamily: "var(--fw-mono)", fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: "var(--fw-muted)" }}>
      {title}
    </div>
    {children}
  </div>
);

const Editor = ({ form, setForm, onSubmit, onCancel, submitting, error, mode }) => {
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} style={{ display: "grid", gap: 18 }}>
      <Section title="Card essentials">
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "120px 1fr 100px", gap: 12 }}>
          <Field label="Tag"><input className="fw-input" value={form.tag} onChange={change("tag")} placeholder="CASE 014" required /></Field>
          <Field label="Title"><input className="fw-input" value={form.title} onChange={change("title")} required /></Field>
          <Field label="Order"><input className="fw-input" type="number" value={form.order} onChange={change("order")} /></Field>
        </div>
        <Field label="Description"><input className="fw-input" value={form.description} onChange={change("description")} /></Field>
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12 }}>
          <Field label="Card color">
            <input className="fw-input" type="color" value={form.color} onChange={change("color")} style={{ height: 42, padding: 4 }} />
          </Field>
          <Field label="Published">
            <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8, fontSize: 13 }}>
              <input type="checkbox" checked={!!form.published} onChange={change("published")} />
              Visible on public site
            </label>
          </Field>
        </div>
      </Section>

      <Section title="Meta strip">
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px 1fr 100px", gap: 12 }}>
          <Field label="Client"><input className="fw-input" value={form.client} onChange={change("client")} /></Field>
          <Field label="Industry"><input className="fw-input" value={form.industry} onChange={change("industry")} /></Field>
          <Field label="Year"><input className="fw-input" type="number" value={form.year} onChange={change("year")} /></Field>
          <Field label="Location"><input className="fw-input" value={form.location} onChange={change("location")} /></Field>
          <Field label="Weeks"><input className="fw-input" type="number" value={form.durationWeeks} onChange={change("durationWeeks")} /></Field>
        </div>
      </Section>

      <Section title="Narrative">
        <Field label="Challenge"><textarea className="fw-textarea" rows={3} value={form.challenge} onChange={change("challenge")} /></Field>
        <Field label="Approach"><textarea className="fw-textarea" rows={3} value={form.approach} onChange={change("approach")} /></Field>
        <Field label="Outcome"><textarea className="fw-textarea" rows={3} value={form.outcome} onChange={change("outcome")} /></Field>
      </Section>

      <Section title="Lists (one per line)">
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Results"><textarea className="fw-textarea" rows={5} value={form.resultsText} onChange={change("resultsText")} placeholder="+120% revenue&#10;Sub-1s checkout" /></Field>
          <Field label="Deliverables"><textarea className="fw-textarea" rows={5} value={form.deliverablesText} onChange={change("deliverablesText")} /></Field>
          <Field label="Tech stack"><textarea className="fw-textarea" rows={5} value={form.techStackText} onChange={change("techStackText")} /></Field>
        </div>
      </Section>

      <Section title="Media">
        <Field label="Hero image">
          <ImageUploader
            value={form.heroImageUrl}
            onChange={(url) => setForm((f) => ({ ...f, heroImageUrl: url }))}
          />
        </Field>
        <Field label="Gallery">
          <ImageGalleryUploader
            value={linesToArray(form.galleryText)}
            onChange={(urls) => setForm((f) => ({ ...f, galleryText: arrayToLines(urls) }))}
          />
        </Field>
      </Section>

      <Section title="Testimonial">
        <Field label="Quote"><textarea className="fw-textarea" rows={2} value={form.testimonialQuote} onChange={change("testimonialQuote")} /></Field>
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Author"><input className="fw-input" value={form.testimonialAuthor} onChange={change("testimonialAuthor")} /></Field>
          <Field label="Role"><input className="fw-input" value={form.testimonialRole} onChange={change("testimonialRole")} /></Field>
        </div>
      </Section>

      <Section title="Links">
        <LinksEditor
          value={form.links || []}
          onChange={(links) => setForm((f) => ({ ...f, links }))}
        />
      </Section>

      {error && <div style={{ color: "var(--fw-red)", fontSize: 13 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 8, borderTop: "1px solid var(--fw-line)" }}>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={submitting}>
          {submitting ? "Saving…" : mode === "create" ? "Create case study" : "Save changes"}
        </button>
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const CaseStudiesSection = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(
    () =>
      subscribeAllCaseStudies((v) => {
        setItems(v);
        setLoaded(true);
      }),
    []
  );

  const showSkeleton = useMinLoading(!loaded);

  const mode = editing === "new" ? "create" : editing ? "edit" : null;

  const startNew = () => { setError(null); setForm(empty); setEditing("new"); };
  const startEdit = (cs) => { setError(null); setForm(fromDoc(cs)); setEditing(cs.id); };

  const save = async () => {
    setError(null); setSubmitting(true);
    try {
      const payload = toPayload(form);
      if (mode === "create") await createCaseStudy(payload);
      else await updateCaseStudy(editing, payload);
      setEditing(null); setForm(empty);
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const remove = async (cs) => {
    if (!window.confirm(`Delete "${cs.title}"? This cannot be undone.`)) return;
    await deleteCaseStudy(cs.id);
    if (editing === cs.id) setEditing(null);
  };

  return (
    <Card
      title={
        editing
          ? mode === "create"
            ? "New case study"
            : `Editing · ${form.title || "case study"}`
          : `Case studies (${items.length})`
      }
      action={editing ? null : <button className="fw-btn fw-btn--primary" onClick={startNew}>New case study</button>}
    >
      {editing ? (
        <Editor
          form={form}
          setForm={setForm}
          onSubmit={save}
          onCancel={() => { setEditing(null); setError(null); }}
          submitting={submitting}
          error={error}
          mode={mode}
        />
      ) : showSkeleton ? (
        <SkeletonList count={4} ariaLabel="Loading case studies">
          <SkeletonRow cols={5} widths={["44px", "1fr", "80px", "80px", "180px"]} />
        </SkeletonList>
      ) : items.length === 0 ? (
        <Empty>No case studies yet</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((cs) => (
            <div
              key={cs.id}
              style={{
                display: "grid",
                gridTemplateColumns: "44px 1fr auto auto auto auto",
                gap: 12,
                alignItems: "center",
                padding: "14px 0",
                borderBottom: "1px solid var(--fw-line)",
              }}
            >
              <span
                style={{
                  width: 36, height: 36, borderRadius: 4,
                  background: cs.color || "#1f1f1f",
                  border: "1px solid var(--fw-line-strong)",
                }}
                title={cs.color}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {cs.title || <em style={{ color: "var(--fw-muted)" }}>Untitled</em>}
                </div>
                <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)", marginTop: 2 }}>
                  {cs.tag || "—"} · {cs.client || "no client"} · {cs.year || "—"}
                </div>
              </div>
              <StageChip status={cs.published ? "paid" : "pending"} />
              <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                ord {cs.order ?? "—"}
              </span>
              <button
                className="fw-btn fw-btn--ghost"
                style={{ padding: "6px 10px" }}
                onClick={() => setPublished(cs.id, !cs.published)}
              >
                {cs.published ? "Unpublish" : "Publish"}
              </button>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }} onClick={() => startEdit(cs)}>Edit</button>
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "var(--fw-red)", borderColor: "var(--fw-red)" }} onClick={() => remove(cs)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default CaseStudiesSection;
